import type { EvaluationEnvelope } from "@/types/evaluation";
import { generatePdf, type PdfSection } from "@shared/utils/pdf-export";

type Nested = Record<string, unknown>;
const asNested = (v: unknown): Nested => (v && typeof v === "object" ? (v as Nested) : {});

const yn = (v: unknown) => (v === true || v === "si" || v === "Si" ? "Sí" : v === false || v === "no" ? "No" : String(v ?? ""));

export async function exportEvaluationPdf(evalu: EvaluationEnvelope): Promise<void> {
  const adm  = asNested(evalu.administrativos);
  const clin = asNested(evalu.clinicos);
  const ant  = asNested(evalu.antecedentes_detallados);
  const chk  = asNested(evalu.chequeo);
  const gest = asNested(evalu.gestionEvaluador);

  const sections: PdfSection[] = [
    {
      title: "Paciente y programación",
      fields: [
        { label: "Documento",          value: evalu.doc },
        { label: "Nombre",             value: evalu.nombre },
        { label: "Cirugía",            value: evalu.cirugia },
        { label: "Fecha programada",   value: evalu.fecha },
        { label: "Teléfono",           value: evalu.telefono },
        { label: "Programador",        value: evalu.programador },
        { label: "Evaluador",          value: evalu.evaluadorNombre || evalu.evaluador },
        { label: "Fecha de registro",  value: evalu.fechaRegistro },
        { label: "CIE-10",             value: `${evalu.cie10 || ""} · ${evalu.diagnostico || ""}` },
        { label: "Observaciones",      value: evalu.observaciones },
      ],
    },
    {
      title: "Administrativos",
      fields: [
        { label: "Orden médica",              value: yn(adm.orden) },
        { label: "Copago",                    value: yn(adm.copago) },
        { label: "Insumo",                    value: yn(adm.insumo) },
        { label: "Autorización",              value: yn(adm.autorizacion) },
        { label: "Acompañante",               value: yn(adm.acompanante) },
        { label: "Ayuno",                     value: yn(adm.ayuno) },
        { label: "Suspensión meds confirmada",value: yn(adm.suspensionMedConfirm) },
        { label: "Disponibilidad postop.",    value: yn(adm.dispPostop) },
      ],
    },
    {
      title: "Signos vitales y clínico",
      fields: [
        { label: "Presión arterial",   value: `${clin.ps || ""}/${clin.pd || ""} mmHg  (${yn(clin.paControlada)} controlada)` },
        { label: "Frecuencia cardíaca",value: clin.fc },
        { label: "Saturación O₂",      value: clin.saturacion },
        { label: "Temperatura",        value: clin.temp },
        { label: "Glicemia",           value: `${clin.glicemia ?? ""} mg/dL  (${yn(clin.glicemiaControlada)} controlada)` },
        { label: "Peso · Talla",       value: `${clin.peso || "?"} kg · ${clin.talla || "?"} cm` },
        { label: "Blefaritis",         value: clin.blefaritis },
        { label: "Respiratorio",       value: clin.resp },
        { label: "Piel",               value: clin.piel },
        { label: "Dental",             value: clin.dental },
        { label: "Conjuntivitis",      value: clin.conjuntivitis },
        { label: "Estado higiénico",   value: clin.estadoHigienico },
      ],
    },
    {
      title: "Historia clínica",
      fields: [
        { label: "Antecedentes",       value: evalu.antecedentes },
        { label: "Medicamentos",       value: evalu.medicamentos },
        { label: "Alergias",           value: evalu.alergias },
        { label: "Cirugías previas",   value: evalu.cirugiasPrevias },
        { label: "Anticoagulante",     value: ant.anticoagulante },
        { label: "Aspirina suspendida",value: ant.aspirina_suspendida },
        { label: "Autoriz. anestesio.",value: ant.autoriz_anest_anticoag },
      ],
    },
    {
      title: "Chequeo pre-quirúrgico",
      fields: [
        { label: "Modalidad",              value: chk.modalidad },
        { label: "Comprende procedimiento",value: yn(chk.orientComprende) },
        { label: "Sabe hora/preparación",  value: yn(chk.orientHoraAntic) },
        { label: "Conoce signos alarma",   value: yn(chk.orientSignosAlarma) },
      ],
    },
    {
      title: "Resultado y decisión",
      fields: [
        { label: "Estado administrativo",  value: evalu.estadoAdministrativo },
        { label: "Resultado clínico",      value: evalu.resultadoClinico },
        { label: "Decisión final",         value: evalu.accionFinal },
        { label: "Requiere gestión",       value: yn(gest.requiereGestion) },
        { label: "Tipo de gestión",        value: gest.tipoGestion },
        { label: "Acciones evaluador",     value: gest.accionesEvaluador },
        { label: "Profesional consultado", value: gest.profesionalConsultado },
        { label: "Respuesta recibida",     value: gest.respuestaRecibida },
        { label: "Evidencia de cierre",    value: gest.evidenciaCierre },
      ],
    },
  ];

  await generatePdf({
    title: `Valoración preoperatoria — ${evalu.nombre || evalu.doc || "sin nombre"}`,
    subtitle: `Documento ${evalu.doc || "—"}  ·  ${evalu.cirugia || "sin cirugía"}`,
    filename: `valoracion-${evalu.doc || evalu.id || "sin-doc"}.pdf`,
    sections,
    footer: "FOCA · Sistema de Valoración Preoperatoria",
    signature: {
      evaluadorNombre: String(evalu.evaluadorNombre || ""),
      evaluadorUsuario: String(evalu.evaluador || ""),
      fechaRegistro: String(evalu.fechaRegistro || ""),
      decision: String(evalu.accionFinal || ""),
    },
  });
}
